// Modal per aprire un nuovo ticket di supporto. Estratto da SupportSection
// per essere riusabile globalmente (es. trigger nel topbar).
//
// Il submit fa due insert in cascata: support_tickets (RLS gated da
// is_active_founder) + support_messages con il body iniziale. Se l'utente
// non e' founder, RLS rifiuta con code 42501 e il modal mostra il messaggio
// "Solo i founder possono aprire ticket".
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LifeBuoy, Loader2 } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Textarea } from "@v2/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@v2/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { captureDebugSnapshot, type DebugSnapshot } from "../../lib/debug-snapshot";

type Category = "bug" | "account" | "team" | "ea" | "other";

export function SupportNewTicketDialog({
  open,
  onOpenChange,
  activeTeamId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  activeTeamId: string | null;
  onCreated?: () => void;
}) {
  const { t } = useTranslation("support");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<Category>("bug");
  const [body, setBody] = useState("");
  const [includeDebug, setIncludeDebug] = useState(true);
  const [debugPreview, setDebugPreview] = useState<DebugSnapshot | null>(null);
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setCategory("bug");
      setBody("");
      setIncludeDebug(true);
      setDebugPreview(null);
      setError(null);
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  const previewDebug = async () => {
    const snap = await captureDebugSnapshot({ active_team_id: activeTeamId });
    setDebugPreview(snap);
    setDebugDialogOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (title.trim().length < 3 || body.trim().length < 1) return;
    setSubmitting(true);
    try {
      const { data: u } = await supabaseV2.auth.getUser();
      if (!u.user) throw new Error(t("modal.needLogin"));
      const snap = includeDebug ? await captureDebugSnapshot({ active_team_id: activeTeamId }) : null;
      const { data: ticket, error: insertErr } = await supabaseV2
        .from("support_tickets")
        .insert({
          user_id: u.user.id,
          team_id: activeTeamId || null,
          title: title.trim(),
          category,
          debug_snapshot: snap,
        })
        .select("id")
        .single();
      if (insertErr) {
        if ((insertErr as any).code === "42501" || /policy/i.test(insertErr.message)) {
          throw new Error(t("modal.founderOnly"));
        }
        throw insertErr;
      }
      const { error: msgErr } = await supabaseV2
        .from("support_messages")
        .insert({
          ticket_id: (ticket as any).id,
          author_user_id: u.user.id,
          is_admin: false,
          body: body.trim(),
        });
      if (msgErr) throw msgErr;
      setSuccess(true);
      onCreated?.();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" />
            {t("modal.title")}
          </DialogTitle>
          <DialogDescription>{t("modal.description")}</DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="space-y-3">
            <div className="rounded-xl bg-emerald-50 text-emerald-800 p-3">
              <p className="font-semibold text-sm">{t("modal.successTitle")}</p>
              <p className="text-xs mt-1">{t("modal.successBody")}</p>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>{t("modal.diagnosticsDialogClose")}</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("modal.ticketTitle")}</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("modal.ticketTitlePh")}
                maxLength={140}
                required
                minLength={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("modal.category")}</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as Category)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">{t("modal.categories.bug")}</SelectItem>
                  <SelectItem value="account">{t("modal.categories.account")}</SelectItem>
                  <SelectItem value="team">{t("modal.categories.team")}</SelectItem>
                  <SelectItem value="ea">{t("modal.categories.ea")}</SelectItem>
                  <SelectItem value="other">{t("modal.categories.other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("modal.body")}</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t("modal.bodyPh")}
                rows={5}
                maxLength={5000}
                required
              />
            </div>
            <div className="rounded-2xl bg-background p-3 space-y-1.5">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeDebug}
                  onChange={(e) => setIncludeDebug(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                {t("modal.diagnosticsToggle")}
              </label>
              <p className="text-xs text-muted-foreground">{t("modal.diagnosticsHint")}</p>
              {includeDebug && (
                <button
                  type="button"
                  onClick={previewDebug}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  {t("modal.diagnosticsSeeDetails")}
                </button>
              )}
            </div>
            {error && (
              <div className="rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2">
                {t("modal.errorPrefix", { message: error })}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                {t("modal.cancel")}
              </Button>
              <Button type="submit" disabled={submitting || title.trim().length < 3 || body.trim().length < 1}>
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" />{t("modal.submitting")}</> : t("modal.submit")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>

      {/* Dialog interno per anteprima snapshot */}
      <Dialog open={debugDialogOpen} onOpenChange={setDebugDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("modal.diagnosticsDialogTitle")}</DialogTitle>
          </DialogHeader>
          <pre className="text-xs font-mono bg-slate-950 text-slate-200 rounded-md p-3 max-h-[60vh] overflow-auto">
            {JSON.stringify(debugPreview, null, 2)}
          </pre>
          <DialogFooter>
            <Button onClick={() => setDebugDialogOpen(false)}>{t("modal.diagnosticsDialogClose")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
